import { useModuleBEM } from "@b2bportal/core-themes";
import { IconNameEnum } from "@b2bportal/core-themes";
import type { ActivityReservation } from "@b2bportal/rbc-activities-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TripAdvisorRating } from "@tenant/activities";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import styles from "./ActivityDetailsPanel.module.scss";
import { useActivityDetailsPanel } from "./hook";

interface ActivityDetailsPanelProps {
  reservation: ActivityReservation;
  className?: string;
}

export const ActivityDetailsPanel = ({
  reservation,
  className,
}: ActivityDetailsPanelProps) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "ActivityDetailsPanel");

  const {
    name,
    duration,
    image,
    rating,
    cancellationPolicy,
    voucherUrl,
    languageGuide,
    startTimeLabel,
    endTimeLabel,
    travelerLabel,
  } = useActivityDetailsPanel(reservation);

  return (
    <div className={clsx(block, className)}>
      {image && (
        <div className={cn("image-wrapper")}>
          <img
            className={cn("image")}
            src={image}
            alt={t("activities.postBooking.details.altDescription", {
              activityName: name,
            })}
          />
        </div>
      )}
      <div className={cn("content")}>
        {rating && (
          <div className={cn("rating")}>
            <TripAdvisorRating rating={rating} />
          </div>
        )}
        <h2 className={cn("name")}>{name}</h2>

        <div className={cn("details-grid")}>
          {startTimeLabel && (
            <div className={cn("grid-row")}>
              <span className={cn("grid-label")}>
                {t("activities.postBooking.startTime")}
              </span>
              <span className={cn("grid-value")}>{startTimeLabel}</span>
            </div>
          )}
          {endTimeLabel && (
            <div className={cn("grid-row")}>
              <span className={cn("grid-label")}>
                {t("activities.postBooking.endTime")}
              </span>
              <span className={cn("grid-value")}>{endTimeLabel}</span>
            </div>
          )}
          {duration && (
            <div className={cn("grid-row")}>
              <span className={cn("grid-label")}>
                {t("activities.postBooking.duration")}
              </span>
              <span className={cn("grid-value")}>{duration}</span>
            </div>
          )}
          {travelerLabel && (
            <div className={cn("grid-row")}>
              <span className={cn("grid-label")}>
                {t("activities.postBooking.numberOfTravelers")}
              </span>
              <span className={cn("grid-value")}>{travelerLabel}</span>
            </div>
          )}
          {languageGuide && (
            <div className={cn("grid-row")}>
              <span className={cn("grid-label")}>
                {t("activities.postBooking.tourLanguage")}
              </span>
              <span className={cn("grid-value")}>{languageGuide}</span>
            </div>
          )}
          {voucherUrl && (
            <div className={cn("voucher-row")}>
              <a
                className={cn("voucher-link")}
                href={voucherUrl}
                target="_blank"
                rel="noreferrer"
              >
                {t("activities.postBooking.details.goToVoucher")}
                <Icon
                  iconName={IconNameEnum.externalLink}
                  className={cn("voucher-icon")}
                />
              </a>
            </div>
          )}
        </div>

        {cancellationPolicy && (
          <div className={cn("cancellation-banner")}>
            <Icon
              iconName={IconNameEnum.stays__security}
              className={cn("cancellation-icon")}
            />
            <span className={cn("cancellation-text")}>
              {cancellationPolicy}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
